/**
 * EL LECTOR DE CÓDIGOS, EN TODAS LAS PANTALLAS Y SIN TOCAR NADA (2026-10-03).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LO QUE PIDIÓ EL DUEÑO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * «¿No hay una forma de que no sea necesario tocar la pantalla, y en todos
 * lados, y que no tengas que dar un clic ni abrir el teclado para que
 * funcione?»
 *
 * Hasta ahora el código solo llegaba si una casilla tenía el cursor (el
 * buscador de Vender, el de Entradas, la casilla invisible del Consultor). Si
 * el cursor estaba en otro sitio —se tocó un botón, se abrió el menú— el
 * código se perdía sin avisar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  CÓMO: SE ESCUCHA EL TECLADO DE LA PANTALLA ENTERA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Un lector en modo «teclado» —el USB, el Bluetooth, y el incluido en un
 * teléfono si su salida es «Teclado»— manda cada número como una tecla, muy
 * rápido, y un Enter al final. Eso llega a la página aunque ninguna casilla
 * tenga el cursor. Aquí se reconoce por la VELOCIDAD (una persona no teclea
 * una tecla cada pocos milisegundos) y se le entrega a la pantalla que esté
 * abierta:
 *
 *   · la que pidió los códigos con `useAlLeerCodigo` (Vender, Catálogo,
 *     Inventario, Entrada de mercancía…), la de más arriba si hay varias;
 *   · y si ninguna los pidió, sale la CONSULTA RÁPIDA: el producto y su
 *     precio, con «Vender» y «Ver en el catálogo». Así el lector sirve en
 *     cualquier pantalla de Softfi.
 *
 * Si una casilla tiene el cursor, lo que se teclea es suyo y aquí no se toca.
 * Con una ventana abierta, solo la recibe quien la pidió desde esa ventana.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  Y EL OTRO MODO, SOLO, SIN AJUSTES (2026-10-03)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El lector de algunos teléfonos, en modo «escribir en la casilla», no teclea:
 * escribe en la casilla que recibió el cursor con un toque. Se pensó en una
 * página «Ajustes › Lector de códigos» que dijera cómo pasarlo a «Teclado»; el
 * dueño prefirió que Softfi reconociera los dos modos solo. Así que, además de
 * escuchar el teclado, `LectorGlobal` tiene una casilla invisible que toma el
 * cursor con cada toque en la pantalla (sin abrir el teclado: `inputMode=
 * "none"`). Lo que escribe ahí el lector llega por el mismo camino que lo
 * tecleado: `entregar`. Ver `CasillaDelLector` en `components/LectorGlobal.tsx`.
 */

import { useEffect, useRef, type RefObject } from "react";

/** Entre dos teclas del lector pasan pocos milisegundos; una persona tarda más. */
export const ENTRE_TECLAS_MS = 60;
/** Menos de esto no es un código de barras. */
export const LARGO_MINIMO = 4;
/** Lectores sin Enter al final: se lee cuando dejan de llegar teclas. */
export const PAUSA_SIN_ENTER_MS = 150;
/** Sin Enter, hace falta un código largo para no confundirlo con nada. */
export const LARGO_SIN_ENTER = 6;

type Receptor = { fn: (codigo: string) => void; dentroDeVentana: boolean };

const receptores: Receptor[] = [];

/**
 * Esta pantalla recibe los códigos que lea el lector mientras esté abierta.
 *
 * `dentroDeVentana`: la pide una ventana (un `Modal`) y los recibe aunque esté
 * abierta encima de todo, como la Entrada de mercancía.
 */
export function useAlLeerCodigo(
  alLeer: (codigo: string) => void,
  { activo = true, dentroDeVentana = false }: { activo?: boolean; dentroDeVentana?: boolean } = {},
) {
  const ref = useRef(alLeer);
  ref.current = alLeer;
  useEffect(() => {
    if (!activo) return;
    const receptor: Receptor = { fn: (c) => ref.current(c), dentroDeVentana };
    receptores.push(receptor);
    return () => {
      const i = receptores.lastIndexOf(receptor);
      if (i >= 0) receptores.splice(i, 1);
    };
  }, [activo, dentroDeVentana]);
}

/** Los `<input>` donde no se escribe: un lector no tiene nada que hacer en ellos. */
const SIN_TEXTO = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color", "file", "image"]);

/**
 * ¿Es una casilla donde se escribe? Lo que se teclea ahí es suyo.
 *
 * Una casilla de marcar (`checkbox`) o un botón no cuentan: con el cursor en
 * una de ellas, el lector se escucha igual.
 */
export function aceptaTexto(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLInputElement) return !SIN_TEXTO.has(el.type);
  return el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement || el.isContentEditable;
}

function hayVentanaAbierta(): boolean {
  return document.querySelector('[role="dialog"][aria-modal="true"]') !== null;
}

/**
 * A quién le toca el código: la pantalla de más arriba, o nadie.
 *
 * Devuelve `false` si nadie lo recibió (una ventana abierta que no lo pidió):
 * el teléfono de la PC lo dice en pantalla, porque quien escanea con él no ve
 * que el código se quedó sin sitio (ver `lib/telefonoDeLaPc.ts`).
 */
export function entregar(codigo: string, alNoHaberQuien: (codigo: string) => void): boolean {
  const arriba = receptores.at(-1);
  if (hayVentanaAbierta()) {
    // Con una ventana encima, solo la ventana que lo pidió: nunca la pantalla
    // de debajo (escanear durante el cobro no puede sumar otro producto).
    if (!arriba?.dentroDeVentana) return false;
    arriba.fn(codigo);
    return true;
  }
  if (arriba) arriba.fn(codigo);
  else alNoHaberQuien(codigo);
  return true;
}

/**
 * Lo que escucha el teclado de la pantalla entera. Va una sola vez, en
 * `AppLayout` (ver `components/LectorGlobal.tsx`).
 */
export function useEscucharElLector(alLeer: (codigo: string) => void) {
  const alLeerRef = useRef(alLeer);
  alLeerRef.current = alLeer;

  useEffect(() => {
    let buffer = "";
    let ultima = 0;
    let espera: number | undefined;

    const soltar = () => {
      const codigo = buffer;
      buffer = "";
      if (codigo.length >= LARGO_MINIMO) alLeerRef.current(codigo);
    };

    const alTeclear = (e: KeyboardEvent) => {
      // Lo que se escribe en una casilla es de la casilla (también la
      // invisible del lector, que lee por su cuenta).
      if (aceptaTexto(e.target)) {
        buffer = "";
        return;
      }
      const ahora = performance.now();
      const seguido = ahora - ultima <= ENTRE_TECLAS_MS;

      if (e.key === "Enter" || e.key === "Tab") {
        window.clearTimeout(espera);
        if (buffer.length >= LARGO_MINIMO && seguido) {
          // ⚠️ El Enter del lector no puede «pulsar» el botón que tenía el
          // cursor: si era «Cobrar», cobraría.
          e.preventDefault();
          e.stopPropagation();
          soltar();
        } else {
          buffer = "";
        }
        return;
      }
      if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;

      if (!seguido) buffer = "";
      ultima = ahora;
      buffer += e.key;

      // Sin Enter: se lee cuando dejan de llegar teclas, si es largo.
      window.clearTimeout(espera);
      espera = window.setTimeout(() => {
        if (buffer.length >= LARGO_SIN_ENTER) soltar();
        else buffer = "";
      }, PAUSA_SIN_ENTER_MS);
    };

    // En captura: antes de que un botón con el cursor reciba el Enter.
    window.addEventListener("keydown", alTeclear, true);
    return () => {
      window.removeEventListener("keydown", alTeclear, true);
      window.clearTimeout(espera);
    };
  }, []);
}

// =============================================================================
//  EL GATILLO DEL LECTOR QUE «RELLENA» LA CASILLA (2026-10-03)
// =============================================================================
//
// ⚠️ LO QUE ENSEÑÓ LA PRUEBA EN EL APARATO (`/prueba-del-lector`)
//
// El teléfono con lector incluido del cliente, en su modo de fábrica («direct
// fill»), NO teclea el código: lo escribe de una sola vez en la casilla que
// tiene el cursor, y SOLO si esa casilla tiene el teclado activo. En una con
// `inputMode="none"` (la del Consultor, la del buscador de Vender esperando al
// lector) no escribe nada, y sin casilla tampoco. Por eso no funcionaba.
//
// Pero el gatillo avisa: mientras se aprieta, llegan pulsaciones sin nombre
// (`key="Unidentified"`, código 0), y el código llega unos 300 ms después. En
// ese instante se le da el cursor a una casilla que SÍ acepta texto
// (`inputmode="text"`), pidiéndole al navegador que no saque el teclado
// (`virtualkeyboardpolicy="manual"`). Al leer, vuelve a `inputmode="none"`.
// Así funciona sin que nadie toque la configuración del lector.

/** La pulsación del gatillo de un teléfono con lector: sin nombre y con código 0. */
export function esElGatillo(e: KeyboardEvent): boolean {
  return e.key === "Unidentified" && e.keyCode === 0;
}

/** ¿El código, al llegar, se escribiría ahí? En una casilla de texto que no sea `inputMode="none"`. */
function escribiriaAhi(el: Element | null): boolean {
  return aceptaTexto(el) && !(el instanceof HTMLElement && el.getAttribute("inputmode") === "none");
}

/** La casilla, lista para que el lector escriba en ella: con cursor y aceptando texto. */
export function abrirParaElLector(casilla: HTMLInputElement) {
  casilla.setAttribute("virtualkeyboardpolicy", "manual");
  if (casilla.getAttribute("inputmode") !== "text") {
    casilla.setAttribute("inputmode", "text");
    // Con el cursor ya puesto, el navegador no se entera del cambio hasta que
    // la casilla lo vuelve a recibir.
    if (document.activeElement === casilla) casilla.blur();
  }
  if (document.activeElement !== casilla) casilla.focus({ preventScroll: true });
}

/** Después de leer: vuelve a esperar sin teclado. */
export function cerrarParaElLector(casilla: HTMLInputElement) {
  casilla.setAttribute("inputmode", "none");
}

/**
 * Al apretar el gatillo, la casilla del lector recibe el cursor —salvo que ya
 * lo tenga una casilla de texto donde el código se escribiría igual (el
 * buscador que se tocó para escribir)—.
 */
export function useGatilloDelLector(casilla: RefObject<HTMLInputElement | null>, activo = true) {
  useEffect(() => {
    if (!activo) return;
    const alApretar = (e: KeyboardEvent) => {
      if (!esElGatillo(e)) return;
      const c = casilla.current;
      if (!c) return;
      const activa = document.activeElement;
      if (activa !== c && escribiriaAhi(activa)) return;
      abrirParaElLector(c);
    };
    window.addEventListener("keydown", alApretar, true);
    return () => window.removeEventListener("keydown", alApretar, true);
  }, [activo, casilla]);
}
